import React from "react";
import {
  InsightDocument,
  ContentfulContentType,
  PressRelease,
} from "@tiicker/util/lib/contentful/types";
import { useLazyLoadContentfulEntries } from "source/contentful/provider";
import {
  InsightsAssociatedWithBrand,
  InsightsByLatest,
} from "@tiicker/util/lib/contentful/queries";
import { InsightsSidebarList } from "./InsightsSidebarList";
import styles from "./InsightsSidebarList.module.scss";
import { Entry } from "contentful";
import { sortBy } from "lodash";
import { SidebarItem } from "./InsightsSidebarListItem";
import { getImageReference } from "@tiicker/util/lib/contentful/helpers";

type Props = {
  tickerSymbol?: string;
  excludeInsightId?: string;
  relatedInsightIds?: string[];
  authorName?: string;
};

export const convertInsightOrPressReleaseToSidebarItem = (
  item: Entry<InsightDocument> | Entry<PressRelease>
): SidebarItem => {
  const isPressRelease = item.sys.contentType.sys.id === "pressRelease";

  const heroImage = getImageReference(item.fields?.heroImage);

  return {
    url: isPressRelease ? "/news/[slug]" : "/insights/[slug]",
    as: isPressRelease
      ? `/news/${item.fields.slug}`
      : `/insights/${item.fields.slug}`,
    category:
      (item as Entry<InsightDocument>).fields.category ?? "Press Release",
    publishedDate: item.fields.publishedDate,
    title: item.fields.title,
    heroImage: heroImage?.fields.file.url,
  };
};

export const InsightsSidebarArticleSection: React.FC<Props> = (props) => {
  const [insightsRelatedToBrand, relatedLoadingInfo] =
    useLazyLoadContentfulEntries<InsightDocument>(
      ContentfulContentType.Article,
      {
        groupSize: 3,
        query: InsightsAssociatedWithBrand(
          props.tickerSymbol ?? "",
          props.excludeInsightId ? [props.excludeInsightId] : []
        ),
      }
    );

  const [newInsights, newInsightsLoadingInfo] =
    useLazyLoadContentfulEntries<InsightDocument>(
      ContentfulContentType.Article,
      {
        groupSize: 3,
        query: InsightsByLatest(
          props.excludeInsightId ? [props.excludeInsightId] : []
        ),
      }
    );

  // Display the loading placeholders until all articles have been retrieved
  if (insightsRelatedToBrand.state !== "DONE" || newInsights.state !== "DONE") {
    return (
      <>
        {/* <LoadingInsightsSidebarList insightCount={3} />
        <LoadingInsightsSidebarList insightCount={3} /> */}
      </>
    );
  }

  // Combine results from related queries, remove duplicates by id, and remove any duplicates from insightsByBrand query
  const combinedRelatedArticles = insightsRelatedToBrand.result.items;

  const sortedCombinedRelatedArticles = sortBy(
    combinedRelatedArticles,
    (article) => article.fields.publishedDate
  )
    .reverse()
    .slice(0, 3);

  const relatedArticlesAvailable =
    props.tickerSymbol && combinedRelatedArticles.length > 0;

  return (
    <>
      {relatedArticlesAvailable ? (
        <div className={styles.insightsContainer}>
          <InsightsSidebarList
            headerTitle="Related Insights"
            insights={sortedCombinedRelatedArticles.map((i) =>
              convertInsightOrPressReleaseToSidebarItem(i)
            )}
          />
        </div>
      ) : (
        <div className={styles.insightsContainer}>
          <InsightsSidebarList
            headerTitle="New Insights"
            insights={newInsights.result.items.map((i) =>
              convertInsightOrPressReleaseToSidebarItem(i)
            )}
          />
        </div>
      )}
    </>
  );
};
